import {
  BROWSE_STAGE_LABELS,
  currencyCodeSchema,
  formatMoney,
  overBudgetLabel,
  type ApplicationStage,
  type BrowseStage,
  type CandidateApplicationRow,
  type CriterionMark,
} from '@nest-recruit/shared';

// The pure halves of the candidate pop-up (CandidatePanel.dc.html): the salary lines, the stage
// stepper, the tab counts and the criterion marks. Kept out of the components so each rule is tested.

const NOT_GIVEN = 'Not given';
/** More jobs than this and the left side shows two lines and "and N more" (mockup `salaryLines`). */
const MAX_SALARY_LINES = 3;
const SHOWN_WHEN_MORE = 2;

/** Minor units and a code to "৳85,000"; null when either is missing or unreadable (#7). */
export function moneyText(minor: string | null, currency: string | null): string | null {
  const code = currencyCodeSchema.safeParse(currency);
  if (minor === null || !code.success || !/^\d+$/.test(minor)) return null;
  return formatMoney({ amountMinor: BigInt(minor), currency: code.data });
}

/** "৳60,000 – ৳68,000", or "up to ৳68,000" when the job set only a top. */
export function budgetText(row: Pick<CandidateApplicationRow, 'budgetMinMinor' | 'budgetMaxMinor' | 'budgetCurrency'>): string | null {
  const top = moneyText(row.budgetMaxMinor, row.budgetCurrency);
  if (top === null) return null;
  const bottom = moneyText(row.budgetMinMinor, row.budgetCurrency);
  return bottom === null ? `up to ${top}` : `${bottom} – ${top}`;
}

type SalaryRow = Pick<
  CandidateApplicationRow,
  | 'expectedSalaryMinor'
  | 'expectedSalaryCurrency'
  | 'budgetMinMinor'
  | 'budgetMaxMinor'
  | 'budgetCurrency'
  | 'expectedSalaryOverBudgetPercent'
>;

export interface ApplicationSalary {
  /** "৳85,000", or "not given". */
  readonly expectsText: string;
  readonly isOver: boolean;
  /** "25% over budget"; empty unless over. */
  readonly overText: string;
  /** The words after the ask: the budget, or why there is nothing to compare. */
  readonly note: string;
}

/**
 * One job card's salary line (owner, 2026-10-17): the ask against the job's optional budget.
 * The percent comes from the server (`overBudgetPercent`), so the screen never does its own sums.
 */
export function applicationSalary(row: SalaryRow): ApplicationSalary {
  const expects = moneyText(row.expectedSalaryMinor, row.expectedSalaryCurrency);
  if (expects === null) return { expectsText: 'not given', isOver: false, overText: '', note: 'Not given for this job' };
  const budget = budgetText(row);
  if (budget === null) return { expectsText: expects, isOver: false, overText: '', note: 'No budget set for this job' };
  const percent = row.expectedSalaryOverBudgetPercent;
  if (percent !== null) {
    const more = askAboveTop(row);
    return { expectsText: expects, isOver: true, overText: overBudgetLabel(percent), note: more === null ? `Budget ${budget}` : `Budget ${budget} · asks ${more} more than the top` };
  }
  if (row.budgetCurrency !== null && row.expectedSalaryCurrency !== null && row.budgetCurrency !== row.expectedSalaryCurrency) {
    // Amounts in two currencies are never converted to compare (#7).
    return { expectsText: expects, isOver: false, overText: '', note: `Budget ${budget}, in another currency` };
  }
  return { expectsText: expects, isOver: false, overText: '', note: `Within budget (${budget})` };
}

function askAboveTop(row: SalaryRow): string | null {
  if (row.expectedSalaryMinor === null || row.budgetMaxMinor === null) return null;
  if (!/^\d+$/.test(row.expectedSalaryMinor) || !/^\d+$/.test(row.budgetMaxMinor)) return null;
  const difference = BigInt(row.expectedSalaryMinor) - BigInt(row.budgetMaxMinor);
  if (difference <= 0n) return null;
  return moneyText(difference.toString(), row.expectedSalaryCurrency);
}

export interface SalaryLine {
  readonly key: string;
  /** "৳85,000" or "Not given". */
  readonly amount: string;
  /** The job title, or "same for 3 jobs". */
  readonly job: string;
  readonly isOver: boolean;
  readonly overText: string;
  /** "within budget", "closed", "within budget · closed"; empty for none. */
  readonly note: string;
}

export interface SalaryLines {
  readonly lines: readonly SalaryLine[];
  /** How many more sit in Jobs applied. */
  readonly more: number;
}

type SalaryLineRow = SalaryRow & Pick<CandidateApplicationRow, 'id' | 'jobTitle' | 'isLive' | 'appliedAt' | 'canOpen'>;

/**
 * The left side's Expected salary (mockup `salaryLines`): one line per job, live jobs first and
 * newest first; one line when every job has the same figure; two lines and "and N more" past
 * three. Applications outside the viewer's access carry no figure and are left out.
 */
export function salaryLines(rows: readonly SalaryLineRow[]): SalaryLines {
  const newestFirst = rows.filter((row) => row.canOpen).sort((first, second) => second.appliedAt.localeCompare(first.appliedAt));
  const ordered = [...newestFirst.filter((row) => row.isLive), ...newestFirst.filter((row) => !row.isLive)];
  const figures = ordered.map((row) => moneyText(row.expectedSalaryMinor, row.expectedSalaryCurrency));
  const first = figures[0];
  if (ordered.length > 1 && first !== null && first !== undefined && figures.every((figure) => figure === first)) {
    return { lines: [{ key: 'same', amount: first, job: `same for ${ordered.length} jobs`, isOver: false, overText: '', note: '' }], more: 0 };
  }
  const shown = ordered.length > MAX_SALARY_LINES ? ordered.slice(0, SHOWN_WHEN_MORE) : ordered;
  const lines = shown.map((row): SalaryLine => {
    const salary = applicationSalary(row);
    const notes: string[] = [];
    if (salary.note.startsWith('Within')) notes.push('within budget');
    if (!row.isLive) notes.push('closed');
    return {
      key: row.id,
      amount: moneyText(row.expectedSalaryMinor, row.expectedSalaryCurrency) ?? NOT_GIVEN,
      job: row.jobTitle ?? '—',
      isOver: salary.isOver,
      overText: salary.overText,
      note: notes.join(' · '),
    };
  });
  return { lines, more: ordered.length - shown.length };
}

// --- The stage stepper -----------------------------------------------------------------------

export type StepState = 'done' | 'current' | 'todo' | 'stop';

export interface Step {
  readonly key: string;
  readonly label: string;
  readonly state: StepState;
}

const OUT_STAGES: readonly ApplicationStage[] = ['rejected', 'withdrawn', 'closed_job', 'auto_rejected', 'transferred'];
const INTERVIEW_STAGES: readonly ApplicationStage[] = ['interview_1', 'interview_2', 'interview_3'];
const STEP_ORDER: readonly BrowseStage[] = ['ranking', 'review', 'shortlist', 'contact', 'assessment', 'interview', 'selected'];

/**
 * A job card's stepper (§L33's words): Ranking → Review → Shortlist → Contact → (Assessment) →
 * Interview → Selected. Assessment is drawn only when this application has a test or sits there.
 * The profile does not say where an application that ended was stopped, so an ended one shows
 * its end as a last, stopped step after the steps it never finished.
 */
export function stepsOf(
  row: Pick<CandidateApplicationRow, 'stage' | 'browseStage'>,
  stageLabel: string,
  hasTest: boolean,
): readonly Step[] {
  const order = STEP_ORDER.filter((key) => key !== 'assessment' || hasTest || row.browseStage === 'assessment');
  const labelOf = (key: BrowseStage): string => (key === 'interview' && INTERVIEW_STAGES.includes(row.stage) ? stageLabel : BROWSE_STAGE_LABELS[key]);
  const isOut = OUT_STAGES.includes(row.stage) || row.stage === 'hold';
  const current = isOut || row.browseStage === null || row.browseStage === 'rejected' ? -1 : order.indexOf(row.browseStage);
  const steps = order.map((key, index): Step => {
    let state: StepState = 'todo';
    if (current >= 0 && index < current) state = 'done';
    if (index === current) state = key === 'selected' ? 'done' : 'current';
    return { key, label: labelOf(key), state };
  });
  if (current >= 0) return steps;
  return [...steps, { key: 'end', label: stageLabel, state: 'stop' }];
}

// --- Tabs ------------------------------------------------------------------------------------

export const PANEL_TAB_LABELS = {
  cv: 'CV',
  jobs: 'Jobs applied',
  interviews: 'Interviews',
  tests: 'Tests',
  documents: 'Documents',
  activity: 'Activity',
  details: 'Details',
} as const;
export type PanelTabKey = keyof typeof PANEL_TAB_LABELS;

export interface TabCountInput {
  readonly applications: number;
  readonly interviews: number;
  readonly tests: number;
  readonly documents: number;
  /** The first page of the timeline, once it has loaded. */
  readonly activity: { readonly count: number; readonly hasMore: boolean } | null;
}

/** The number beside each tab name; null shows none (CV, Details, an empty list). */
export function tabCounts(input: TabCountInput): Readonly<Record<PanelTabKey, string | null>> {
  const shown = (count: number): string | null => (count > 0 ? String(count) : null);
  const activity = input.activity === null || input.activity.count === 0 ? null : `${input.activity.count}${input.activity.hasMore ? '+' : ''}`;
  return {
    cv: null,
    jobs: shown(input.applications),
    interviews: shown(input.interviews),
    tests: shown(input.tests),
    documents: shown(input.documents),
    activity,
    details: null,
  };
}

// --- Match score -----------------------------------------------------------------------------

/** §L1's 0–1 match read as a mark: whole is Met, some is Partly, none is Not met. */
export function markOfMatch(match: number): CriterionMark {
  if (match >= 1) return 'met';
  if (match > 0) return 'partly';
  return 'not_met';
}

/** "18 of 20". */
export function pointsText(points: number, weight: number): string {
  return `${Math.round(points)} of ${Math.round(weight)}`;
}
